import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { broadcasts } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, SimpleGrid, Skeleton } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, DataTable, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { BarChart } from '@repletoryreach/sdk-ui/charts';
import { useT } from '../../../i18n/I18nProvider';
import { useMetaBilling } from '../../../lib/meta-billing';
import { formatCount, formatDate, type Indexed } from '../../../lib/format';
import { formatRate, rate, reasonText } from '../components/delivery';
import '../broadcasts.css';

const DAYS = [7, 30, 90] as const;
const CHANNELS: broadcasts.BroadcastChannel[] = ['whatsapp', 'sms', 'email'];
const MUTED = 'var(--rr-color-text-muted)';

type DayRow = broadcasts.BroadcastDelivery['by_day'][number] & { id: string };

/**
 * Delivery across every broadcast, by day and channel (4 October 2026): how many each channel was handed, how
 * many it reached, how many read it (WhatsApp only), how many failed and why. The broadcast page answers "how did
 * this one do"; this answers "how are my broadcasts doing", which nothing in the console could say before.
 *
 * Asked for one channel at a time, all three at once, and put together here: switching the channel filter then
 * costs no request, and it only offers the channels this business has actually sent on in the period. (The
 * service's all-channels form failed on 4 October 2026; see the note on `getBroadcastDelivery`.)
 */
export default function BroadcastDeliveryPage() {
  const t = useT();
  const payer = useMetaBilling().onlyOwnAccount ? 'customer' as const : undefined;
  const [params, setParams] = useSearchParams();
  const daysParam = Number(params.get('days'));
  const days = (DAYS as readonly number[]).includes(daysParam) ? daysParam : 30;
  const channelParam = params.get('channel') as broadcasts.BroadcastChannel | null;
  const set = (key: string, value: string | null) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    if (value === null) q.delete(key); else q.set(key, value);
    return q;
  }, { replace: true });

  const perChannel = useQueries({
    queries: CHANNELS.map((c) => ({
      queryKey: ['broadcasts', 'delivery', days, c],
      queryFn: () => broadcasts.getBroadcastDelivery({ days, channel: c }),
      retry: false
    }))
  });
  const q = {
    isPending: perChannel.some((x) => x.isPending),
    // One channel failing still shows the others; only all three failing is an error.
    error: perChannel.every((x) => x.isError) ? perChannel[0].error : null
  };
  const d = useMemo(() => {
    const parts = perChannel.map((x) => x.data?.data).filter((x): x is broadcasts.BroadcastDelivery => !!x);
    return {
      totals: parts.flatMap((p) => p.totals),
      by_day: parts.flatMap((p) => p.by_day),
      failures: parts.flatMap((p) => p.failures)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perChannel.map((x) => x.dataUpdatedAt).join(',')]);

  const used = useMemo(() => CHANNELS.filter((c) => (d?.totals ?? []).some((x) => x.channel === c)), [d]);
  const channel = channelParam && used.includes(channelParam) ? channelParam : null;
  const totals = (d?.totals ?? []).filter((x) => !channel || x.channel === channel);
  const byDay = (d?.by_day ?? []).filter((x) => !channel || x.channel === channel);
  const failures = (d?.failures ?? []).filter((x) => !channel || x.channel === channel);

  // One bar per day, across the chosen channels: delivered, failed and still awaiting a report.
  const chart = useMemo(() => {
    const map = new Map<string, { day: string; delivered: number; failed: number; awaiting: number }>();
    for (const r of byDay) {
      const row = map.get(r.day) ?? { day: r.day, delivered: 0, failed: 0, awaiting: 0 };
      row.delivered += r.delivered;
      row.failed += r.failed;
      row.awaiting += r.awaiting_report;
      map.set(r.day, row);
    }
    return [...map.values()].sort((a, b) => a.day.localeCompare(b.day));
  }, [byDay]);

  const tableRows: Indexed<DayRow>[] = byDay
    .slice()
    .sort((a, b) => b.day.localeCompare(a.day) || a.channel.localeCompare(b.channel))
    .map((r) => ({ ...r, id: `${r.day}-${r.channel}` })) as Indexed<DayRow>[];

  const channelName = (c: broadcasts.BroadcastChannel) => t(`broadcasts.delivery.channel.${c}`);

  return (
    <Stack gap="lg">
      <BreadcrumbTrail
        items={[{ label: t('broadcasts.title'), href: '/broadcasts' }, { label: t('broadcasts.delivery.title') }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />
      <PageHeader
        title={t('broadcasts.delivery.title')}
        description={t('broadcasts.delivery.description')}
        withDivider={false}
        actions={(
          <div role="group" aria-label={t('broadcasts.delivery.period')}>
            <SegmentedControl
              size="xs"
              value={String(days)}
              onChange={(v) => set('days', v === '30' ? null : v)}
              data={DAYS.map((n) => ({ value: String(n), label: t('broadcasts.delivery.days', { count: n }) }))}
            />
          </div>
        )}
      />

      {q.error && <Alert color="error" role="alert">{t('broadcasts.delivery.loadError')}</Alert>}

      {used.length > 1 && (
        <div className="rr-broadcasts__filters" role="tablist" aria-label={t('broadcasts.delivery.channelFilter')}>
          <button
            type="button"
            role="tab"
            aria-selected={channel === null}
            className={`rr-broadcasts__chip${channel === null ? ' is-active' : ''}`}
            onClick={() => set('channel', null)}
          >
            {t('broadcasts.delivery.allChannels')}
          </button>
          {used.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={channel === c}
              className={`rr-broadcasts__chip${channel === c ? ' is-active' : ''}`}
              onClick={() => set('channel', c)}
            >
              {channelName(c)}
            </button>
          ))}
        </div>
      )}

      {q.isPending ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {[0, 1, 2].map((i) => <Skeleton key={i} height={150} radius="md" />)}
        </SimpleGrid>
      ) : !q.error && totals.length === 0 ? (
        <Card withBorder>
          <EmptyState title={t('broadcasts.delivery.empty')} description={t('broadcasts.delivery.emptyHint', { count: days })} />
        </Card>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {totals.map((c) => (
              <Card key={c.channel} withBorder data-testid={`delivery-total-${c.channel}`}>
                <Stack gap="sm">
                  <Text role="body" fw={600}>{channelName(c.channel)}</Text>
                  <Figure label={t('broadcasts.report.attempted')} value={formatCount(c.attempted)} />
                  <Figure label={t('broadcasts.recipient.delivered')} value={formatCount(c.delivered)} rate={c.delivered_rate} />
                  {c.channel === 'whatsapp' && (
                    <Figure label={t('broadcasts.recipient.read')} value={formatCount(c.read ?? 0)} rate={c.read_rate} />
                  )}
                  <Figure label={t('status.failed')} value={formatCount(c.failed)} rate={c.failed_rate} tone="error" />
                  {c.awaiting_report > 0 && (
                    <Text role="caption" c={MUTED}>{t('broadcasts.report.awaiting', { count: formatCount(c.awaiting_report) })}</Text>
                  )}
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          {chart.length > 1 && (
            <Card withBorder>
              <Stack gap="sm">
                <Text role="body" fw={600}>{t('broadcasts.delivery.chartTitle')}</Text>
                <BarChart
                  data={chart}
                  dataKey="day"
                  stacked
                  h={220}
                  series={[
                    { name: 'delivered', label: t('broadcasts.recipient.delivered'), color: 'var(--rr-color-primary, #0d9488)' },
                    { name: 'awaiting', label: t('broadcasts.delivery.awaitingShort'), color: 'var(--rr-color-text-muted, #94a3b8)' },
                    { name: 'failed', label: t('status.failed'), color: 'var(--rr-color-error, #dc2626)' }
                  ]}
                  valueFormatter={(v) => formatCount(v)}
                  labelFormatter={(v) => formatDate(String(v))}
                />
              </Stack>
            </Card>
          )}

          <Card withBorder noPadding>
            <DataTable<Indexed<DayRow>>
              records={tableRows}
              idAccessor="id"
              emptyState={t('broadcasts.delivery.empty')}
              columns={[
                { accessor: 'day', title: t('broadcasts.delivery.columns.day'), render: (r) => formatDate(r.day) },
                { accessor: 'channel', title: t('broadcasts.delivery.columns.channel'), render: (r) => channelName(r.channel) },
                { accessor: 'attempted', title: t('broadcasts.report.attempted'), textAlign: 'right', render: (r) => formatCount(r.attempted) },
                {
                  accessor: 'delivered',
                  title: t('broadcasts.recipient.delivered'),
                  textAlign: 'right',
                  render: (r) => `${formatCount(r.delivered)}${r.attempted ? ` (${formatRate(rate(r.delivered, r.attempted))})` : ''}`
                },
                {
                  accessor: 'read',
                  title: t('broadcasts.recipient.read'),
                  textAlign: 'right',
                  render: (r) => r.read == null ? <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text> : formatCount(r.read)
                },
                { accessor: 'failed', title: t('status.failed'), textAlign: 'right', render: (r) => formatCount(r.failed) },
                { accessor: 'awaiting_report', title: t('broadcasts.delivery.awaitingShort'), textAlign: 'right', render: (r) => formatCount(r.awaiting_report) }
              ]}
            />
          </Card>

          <Card withBorder>
            <Stack gap="sm">
              <Text role="body" fw={600}>{t('broadcasts.report.whyFailed')}</Text>
              {failures.length === 0 ? (
                <Text role="bodySmall" c={MUTED}>{t('broadcasts.delivery.noFailures')}</Text>
              ) : (
                failures.map((f) => (
                  <Group key={`${f.channel}-${f.code}`} justify="space-between" wrap="nowrap" gap="sm" data-testid="delivery-reason">
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text role="bodySmall">{reasonText(t, f.code, null, payer)}</Text>
                      {!channel && used.length > 1 && <Text role="caption" c={MUTED}>{channelName(f.channel)}</Text>}
                    </Stack>
                    <Text role="bodySmall" fw={600}>{formatCount(f.count)}</Text>
                  </Group>
                ))
              )}
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

function Figure({ label, value, rate: r, tone }: { label: string; value: string; rate?: number | null; tone?: 'error' }) {
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap">
      <Text role="bodySmall" c={MUTED}>{label}</Text>
      <Text role="bodySmall" fw={600} c={tone === 'error' ? 'var(--rr-color-error)' : undefined}>
        {value}
        {r != null && <span style={{ color: MUTED, fontWeight: 400 }}>{` · ${formatRate(r)}`}</span>}
      </Text>
    </Group>
  );
}
